import { useMemo } from 'react'
import { Link } from 'react-router'
import {
  FormattedMessage,
  FormattedRelativeTime,
  FormattedDate
} from 'react-intl'
import {
  getRelativeTimeProps,
  shouldUseRelativeTime
} from 'src/utils/dateUtils'
import AvatarPlaceholder from '@components/AvatarPlaceholder/AvatarPlaceholder'
import RunButton from '@components/RunButton/RunButton'
import type { QueryListItem } from 'src/types/queries'
import type { DashboardListItem } from 'src/types/dashboard'
import PrivacyTag from '@components/PrivacyTag/PrivacyTag'

import './ResourceList.css'

export type ResourceType = 'query' | 'dashboard'

type ResourceListProps =
  | {
      type: 'query'
      items: QueryListItem[]
    }
  | {
      type: 'dashboard'
      items: DashboardListItem[]
    }

export default function ResourceList({ type, items }: ResourceListProps) {
  const basePath = type === 'query' ? 'queries' : 'dashboards'

  const processedItems = useMemo(() => {
    return items.map((item) => {
      const createdIsRecent = shouldUseRelativeTime(item?.created)
      let dateToProcess: string | undefined

      if (type === 'query') {
        dateToProcess = (item as QueryListItem).lastRun
      } else {
        dateToProcess = (item as DashboardListItem).updated
      }

      return {
        ...item,
        lastUpdateProps: dateToProcess
          ? getRelativeTimeProps(dateToProcess)
          : null,
        createdIsRecent,
        createdProps:
          createdIsRecent && item?.created
            ? getRelativeTimeProps(item.created)
            : null
      }
    })
  }, [items, type])

  return (
    <ul id={`${type}-list`} className="resource-list">
      {processedItems?.map((item) => (
        <li key={item.id} className={`${type}-item resource-item`}>
          <div className={`${type}-info resource-info`}>
            <Link
              className={`icon icon-resource-${type}`}
              to={`/${basePath}/${item.id}`}
            />
            <h3 title={item.title} className={`${type}-title resource-title`}>
              <Link className="ellipsis" to={`/${basePath}/${item.id}`}>
                {item.title}
              </Link>
              <PrivacyTag privacy={item.public ? 'public' : 'private'} />
            </h3>
            <div className={`${type}-meta resource-meta`}>
              <AvatarPlaceholder
                className="user-avatar"
                text={item.owner.name}
              />
              <span className="username">{item.owner.name}</span>
              <span className="separator">•</span>
              <span className="meta-text">
                <FormattedMessage
                  id={`${type}-item.lastUpdate`}
                  values={{
                    time: item.lastUpdateProps ? (
                      <FormattedRelativeTime
                        key="time"
                        value={item.lastUpdateProps?.value}
                        unit={item.lastUpdateProps?.unit}
                        numeric="auto"
                      />
                    ) : (
                      <FormattedMessage key="time" id="never" />
                    )
                  }}
                />
              </span>
              <em className="meta-text created">
                (
                <FormattedMessage
                  id={`${type}-item.created`}
                  values={{
                    time:
                      item.createdIsRecent && item.createdProps ? (
                        <FormattedRelativeTime
                          key="time"
                          value={item.createdProps.value}
                          unit={item.createdProps.unit}
                        />
                      ) : (
                        <FormattedDate
                          key="time"
                          value={item.created}
                          year="numeric"
                          month="short"
                          day="numeric"
                        />
                      )
                  }}
                />
                )
              </em>
            </div>
          </div>
          <div className={`${type}-actions resource-actions`}>
            {type === 'query' && <RunButton id={item.id} />}
          </div>
        </li>
      ))}
    </ul>
  )
}
